import * as amplitude from '@amplitude/analytics-browser';
import { Modal } from '@spo/storybook';
import { SpoGendersResponseItem } from '@spo/types';
import { useQueryClient } from '@tanstack/react-query';
import { VpButton } from '@vtmn-play/react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoles } from '../../../contexts/RolesContext';
import { useToast } from '../../../contexts/ToastContext';
import { usePatchSportSettings } from '../../../hooks/mutations/usePatchSportSettings';
import { GenderCheckboxes } from '../genderCheckboxes';
import { SegmentationSelectionInput } from '../segmentationSelectionInput/SegmentationSelectionInput';
import './archi-header-settings.scss';

export interface ArchiHeaderSettingsProps {
  isSportDataLoading: boolean;
  segmentationTypeId: number;
  chosenGenderIds: number[];
  allGenders: SpoGendersResponseItem[];
  sportId: number;
}

export const ArchiHeaderSettings = ({
  isSportDataLoading,
  segmentationTypeId,
  chosenGenderIds,
  allGenders,
  sportId,
}: ArchiHeaderSettingsProps) => {
  const [displayWarning, setDisplayWarning] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedSegmentationTypeId, setSelectedSegmentationTypeId] =
    useState<number>(segmentationTypeId);
  const [selectedGenderIds, setSelectedGenderIds] = useState<number[]>([]);

  const { t } = useTranslation();
  const { addToast } = useToast();
  const { hasRoleSportLeader } = useRoles();

  const queryClient = useQueryClient();

  const mutationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: ['useSportsByIds', [sportId]],
    });
    addToast(
      t('architecture.update-sport-settings.success'),
      'success',
      4500,
      'archi-update-sport-settings-success',
    );
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    !isSportDataLoading && setIsEditing(false);
  }, [isSportDataLoading]);

  const mutationError = () => {
    addToast(
      t('architecture.update-sport-settings.error'),
      'danger',
      4500,
      'archi-update-sport-settings-error',
    );
  };

  const { mutate, isPending } = usePatchSportSettings(
    sportId,
    selectedGenderIds,
    selectedSegmentationTypeId,
    mutationSuccess,
    mutationError,
  );

  const editAndSaveSettings = () => {
    if (isEditing) {
      setDisplayWarning(true);
    } else {
      setIsEditing(true);
    }
    amplitude.track(`Architecture_${isEditing ? 'Save' : 'Edit'}-Settings`);
  };

  const cancelWarning = useCallback(() => {
    setDisplayWarning(false);
    setIsEditing(false);
    setSelectedSegmentationTypeId(segmentationTypeId);
  }, [segmentationTypeId]);

  const footerModal = (
    <>
      <VpButton
        data-testid="archi-warning-update-settings-cancel-button"
        onClick={cancelWarning}
        variant="secondary"
        shape="squared"
      >
        {t('cancel')}
      </VpButton>
      <VpButton
        data-testid="archi-warning-update-settings-save-button"
        onClick={() => {
          mutate();
          setDisplayWarning(false);
        }}
        variant="primary"
        shape="squared"
      >
        {t('architecture.update-sport-settings.warning-confirmation')}
      </VpButton>
    </>
  );

  return (
    <>
      <Modal
        dataTestid="archi-update-settings-warning"
        footer={footerModal}
        isOpen={displayWarning}
        title={t('architecture.modal.title')}
        ariaLabel="Architecture Update Settings Warning Modal"
        onClose={cancelWarning}
      >
        <div className="warning-content" data-testid="warning-content">
          {t('architecture.update-sport-settings.warning')}
        </div>
      </Modal>
      <div className="architecture-settings-align">
        <div className="architecture-settings-container">
          <div className="architecture-leader-sport">
            <p className="leader-sport-label" data-testid="leader-sport-label">
              {t('input.leader-sport_label')}
            </p>
            <p>{t('architecture.not-available')}</p>
          </div>
          <div
            className="architecture-settings"
            data-testid="architecture-settings"
          >
            {Boolean(segmentationTypeId) && (
              <SegmentationSelectionInput
                segmentationId={selectedSegmentationTypeId}
                setSelectedSegmentationTypeId={setSelectedSegmentationTypeId}
                disabled={!isEditing}
              />
            )}
            {allGenders && (
              <GenderCheckboxes
                chosenGenderIds={chosenGenderIds}
                allGenders={allGenders}
                selectedGenderIds={selectedGenderIds}
                setSelectedGenderIds={setSelectedGenderIds}
                disabled={!isEditing}
              />
            )}
          </div>
        </div>
        <div
          className="archi-settings-buttons"
          data-testid="archi-settings-buttons"
        >
          {isEditing && (
            <VpButton
              data-testid="archi-settings_cancel-button"
              shape="squared"
              variant="tertiary"
              size="small"
              onClick={() => {
                setIsEditing(false);
                amplitude.track('Architecture_Cancel-Settings');
              }}
            >
              {t('cancel')}
            </VpButton>
          )}
          {hasRoleSportLeader && (
            <VpButton
              data-testid={`archi-settings_${isEditing ? 'save' : 'edit'}-button`}
              size="small"
              shape="squared"
              onClick={editAndSaveSettings}
              loading={isPending || isSportDataLoading}
            >
              {isEditing ? t('save') : t('architecture.edit-settings')}
            </VpButton>
          )}
        </div>
      </div>
    </>
  );
};
